import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Accordion from '../../shared/Accordion';
import { compose } from '../../utils/compose';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import { withStrictMode } from '../../utils/with-strict-mode';
import ServerStylesConditionalWrapper from '../../shared/ServerStylesConditionalWrapper';
import { device } from '../../../utils/styled-component-mixins';
import useMediaQuery from '../../custom-hooks/useMediaQuery';
import AllianceLocationsLandingSearch from './AllianceLocationsLandingSearch';
import { useLocalization } from '../../custom-hooks/useLocalization';

const AllianceLocationsLandingStyled = styled.div`
  .accordion-content > ul {
    background-color: var(--light-grey);
    padding: 4rem;

    .btn-tertiary > span:first-child {
      font-size: 1.4rem;
      font-weight: 400;
      line-height: 2.4rem;
    }

    li {
      margin-bottom: 2rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    > li {
      margin-bottom: 4rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .locations-list-heading {
    font-size: 2.6rem;
    font-weight: 800;
    line-height: 3.2rem;
    margin-bottom: 4rem;
    text-align: center;

    @media ${device.desktop} {
      font-size: 3rem;
      line-height: 4rem;
    }
  }

  .locations-list {
    display: flex;
    flex-direction: column;
    margin-bottom: 4rem;

    @media ${device.desktop} {
      column-gap: 7.1rem;
      flex-direction: row;
      margin-bottom: 8rem;
    }

    &-column {
      flex: 1 1 100%;

      &:not(:last-child) {
        @media ${device.deviceOnly} {
          .accordion-item:last-child button {
            border-bottom: 0;
          }
        }
      }

      @media ${device.desktop} {
        flex: 1 1 29%;
      }
    }
  }

  .mobile-state-anchors {
    column-gap: 3.2rem;
    row-gap: 2.4rem;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 4rem;

    a {
      line-height: 2.4rem;
    }
  }

  h4 {
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 1rem;
  }

  .alliance-location {
    a,
    p,
    .alliance-type {
      display: block;
      line-height: 2.4rem;
      margin: 0;
    }
  }
`;

const AllianceLocationsLanding = (props) => {
  const {
    heading,
    allianceLocationItems,
    subHeading,
    searchResultsHeading,
    locationListHeading,
    searchMethodConjunctionText,
    currentLocationButtonText,
    searchInputLabel,
    searchResultsDefaultLocationLabel,
    noResultsMessage
  } = props;
  const [statesColumns, setStatesColumns] = useState([]);
  const isDesktop = useMediaQuery(device.desktop);
  const { resourceStrings } = useLocalization();

  const getFirstOccurrenceOfLetter = (state) => {
    const stateFirstLetter = state.state.charAt(0).toUpperCase();
    const indexOfState = allianceLocationItems.findIndex((x) => x.state === state.state);

    const firstStateOccurrenceWithLetter = allianceLocationItems.findIndex(
      (x) => x.state.charAt(0).toUpperCase() === stateFirstLetter
    );

    if (firstStateOccurrenceWithLetter === indexOfState) {
      return state.state.charAt(0);
    }
    return '';
  };

  const createStateColumns = (states) => {
    const numberOfRows = Math.ceil(states.length / 3);
    const columns = [
      { columnIndex: 0, states: [] },
      { columnIndex: 1, states: [] },
      { columnIndex: 2, states: [] }
    ];

    states.forEach((state, index) => {
      if (index <= numberOfRows - 1) {
        columns[0].states = [...columns[0].states, state];
      } else if (index > numberOfRows - 1 && index <= numberOfRows * 2 - 1) {
        columns[1].states = [...columns[1].states, state];
      } else {
        columns[2].states = [...columns[2].states, state];
      }
    });

    return columns;
  };

  useEffect(() => {
    const columns = createStateColumns(allianceLocationItems);
    setStatesColumns(columns);

    document.querySelector('html').style.setProperty('--scroll-padding-top-value', '6.4rem');
  }, []);

  const renderStateLinks = (state) => {
    const letter = getFirstOccurrenceOfLetter(state);
    if (letter) {
      return (
        <a href={`#${letter}`} key={state.stateCode}>
          {letter}
        </a>
      );
    }
    return null;
  };

  return (
    <ServerStylesConditionalWrapper>
      <AllianceLocationsLandingStyled>
        <AllianceLocationsLandingSearch
          heading={heading}
          subHeading={subHeading}
          searchResultsHeading={searchResultsHeading}
          searchMethodConjunctionText={searchMethodConjunctionText}
          currentLocationButtonText={currentLocationButtonText}
          searchInputLabel={searchInputLabel}
          resourceStrings={resourceStrings}
          searchResultsDefaultLocationLabel={searchResultsDefaultLocationLabel}
          noResultsMessage={noResultsMessage}
        />
        <div className="container container-padding-default">
          <h3 className="locations-list-heading">{locationListHeading}</h3>
          {!isDesktop && (
            <div className="mobile-state-anchors">
              {allianceLocationItems.map((state) => renderStateLinks(state))}
            </div>
          )}
          <div className="locations-list">
            {statesColumns.length > 0 &&
              statesColumns.map((statesColumn, columnIndex) => (
                <div className="locations-list-column" key={statesColumn.columnIndex}>
                  {statesColumn.states.map((state, index) => (
                    <Accordion
                      widgetID={getFirstOccurrenceOfLetter(state)}
                      key={state.state}
                      index={`${columnIndex}.${index}`}
                      pageTitle={state.state}
                      className="accordion-item"
                    >
                      <>
                        <ul className="non-bdo-style-list">
                          {state.allianceLocationsByCity.map((city) => (
                            <li key={city.city}>
                              <h4>{city.city}</h4>
                              <ul className="non-bdo-style-list">
                                {city.allianceLocations.map((location) => (
                                  <li
                                    key={`${location.officeName} ${location.phone}`}
                                    className="alliance-location"
                                  >
                                    {location.website ? (
                                      <a href={location.website} target="_blank">
                                        {location.officeName}
                                      </a>
                                    ) : (
                                      <p>{location.officeName}</p>
                                    )}
                                    <div className="alliance-type">{location.bdoAllianceType}</div>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                        </ul>
                      </>
                    </Accordion>
                  ))}
                </div>
              ))}
          </div>
        </div>
      </AllianceLocationsLandingStyled>
    </ServerStylesConditionalWrapper>
  );
};

export default compose(withStrictMode, withLocalization)(AllianceLocationsLanding);
